import type { UseFormReturn } from 'react-hook-form';
import { FX_CURRENCY_PAIRS } from '@metycle/shared';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ComboboxField, type ComboboxOption } from '@/components/combobox-field';
import {
  RAISE_DIRECTIONS,
  RAISE_INSTRUMENTS,
  RAISE_REASON_VALUES,
  RAISE_SCOPES,
  type RaiseFormValues,
} from '../lib/raise-form-schema';
import { BlComboboxField } from './bl-combobox-field';
import { OrderComboboxField } from './order-combobox-field';
import { TargetPriceFields } from './raise-target-price-fields';
import { ScheduleFields } from './raise-schedule-fields';

type Ctrl = UseFormReturn<RaiseFormValues>['control'];

const toOptions = (values: readonly string[]): ComboboxOption[] =>
  values.map((value) => ({ value, label: value }));

// Full metal names (value = the LME hedge code) — matches the launchpad form.
const METAL_OPTIONS: ComboboxOption[] = [
  { value: 'CU', label: 'Copper' },
  { value: 'AL', label: 'Aluminium' },
  { value: 'ZN', label: 'Zinc' },
  { value: 'NI', label: 'Nickel' },
  { value: 'PB', label: 'Lead' },
  { value: 'SN', label: 'Tin' },
];
const BASIS_OPTIONS: ComboboxOption[] = [
  { value: 'LME_CASH', label: 'LME Cash' },
  { value: 'LME_3M', label: 'LME 3M' },
  { value: 'COMEX', label: 'COMEX' },
  { value: 'SHFE', label: 'SHFE' },
];
// value = the Prisma `hedge_request_reason` enum key the form submits; label =
// the @map'd display text. Same value=transport / label=display split as METAL_OPTIONS.
// `satisfies` pins each value to the schema's reason union, so this hand-list
// can't drift out of sync with RAISE_REASON_VALUES without a compile error.
const REASON_OPTIONS = [
  { value: 'Physical_Sale_Pricing', label: 'Physical Sale Pricing' },
  { value: 'Physical_Purchase_Pricing', label: 'Physical Purchase Pricing' },
  { value: 'Price_Fix', label: 'Price Fix' },
  { value: 'Fix_Reversal', label: 'Fix Reversal' },
  { value: 'Roll', label: 'Roll' },
  { value: 'Pre_lending', label: 'Pre-Lending' },
  { value: 'Pre_borrowing', label: 'Pre-Borrowing' },
] satisfies readonly { value: (typeof RAISE_REASON_VALUES)[number]; label: string }[];
const CURRENCY_PAIR_OPTIONS = toOptions(FX_CURRENCY_PAIRS);
const INSTRUMENT_OPTIONS = toOptions(RAISE_INSTRUMENTS);
const DIRECTION_OPTIONS = toOptions(RAISE_DIRECTIONS);
const SCOPE_OPTIONS = toOptions(RAISE_SCOPES);

export function ScopeFields({
  scope,
  orderId,
  blOrderId,
  disabled = false,
  orderOnly = false,
}: Readonly<{
  scope: RaiseFormValues['scopeLevel'];
  orderId: string;
  blOrderId: string;
  disabled?: boolean;
  orderOnly?: boolean;
}>) {
  // Hedge-book launch: only an order picker, no scope selector (launchpad parity).
  if (orderOnly) {
    return (
      <div className="grid grid-cols-1 gap-3">
        <OrderComboboxField currentId={orderId} disabled={disabled} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <ComboboxField<RaiseFormValues>
        name="scopeLevel"
        label="Scope"
        options={SCOPE_OPTIONS}
        searchable={false}
        disabled={disabled}
      />
      {scope === 'ORDER' ? (
        <OrderComboboxField currentId={orderId} disabled={disabled} />
      ) : (
        <BlComboboxField currentId={blOrderId} disabled={disabled} />
      )}
    </div>
  );
}

// Was a free-text input, which let a mistyped pair detach a hedge from its deal
// and report the leg as never hedged (Fizzy #949). Every pair quotes the deal's
// currency against USD, so the options are a closed list.
function CurrencyPairField() {
  return (
    <ComboboxField<RaiseFormValues>
      name="currencyPair"
      label="Currency Pair"
      options={CURRENCY_PAIR_OPTIONS}
      searchable={false}
      required
    />
  );
}

function HedgeQuantityField({ control, isFx }: Readonly<{ control: Ctrl; isFx: boolean }>) {
  return (
    <FormField
      control={control}
      name="quantityMt"
      render={({ field }) => (
        <FormItem className="sm:col-span-2">
          {/* FX hedges are sized by a currency notional, not a metal tonnage. */}
          <FormLabel>{isFx ? 'Hedge Notional *' : 'Hedge Quantity (MT) *'}</FormLabel>
          <FormDescription>
            {isFx
              ? 'Amount of currency to hedge, in the pair currency.'
              : 'Hedge the payable MT (metal content) — not the contract weight, and not a percentage.'}
          </FormDescription>
          <FormControl>
            <Input inputMode="decimal" {...field} data-testid="raise-quantity" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function PrimaryFields({
  control,
  isFx,
  lockDirection = false,
}: Readonly<{ control: Ctrl; isFx: boolean; lockDirection?: boolean }>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {isFx ? (
        <CurrencyPairField />
      ) : (
        <ComboboxField<RaiseFormValues>
          name="hedgeMetal"
          label="Product Metal"
          options={METAL_OPTIONS}
          searchable={false}
          required
        />
      )}
      <ComboboxField<RaiseFormValues>
        name="direction"
        label="Hedge Side"
        options={DIRECTION_OPTIONS}
        searchable={false}
        disabled={lockDirection && !isFx}
      />
      <HedgeQuantityField control={control} isFx={isFx} />
    </div>
  );
}

export function InstrumentFields({ control, isFx }: Readonly<{ control: Ctrl; isFx: boolean }>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <ComboboxField<RaiseFormValues>
        name="instrumentType"
        label="Hedging Type"
        options={INSTRUMENT_OPTIONS}
        searchable={false}
      />
      <ComboboxField<RaiseFormValues>
        name="reason"
        label="Hedge Reason"
        options={REASON_OPTIONS}
        searchable={false}
      />
      <ScheduleFields control={control} isFx={isFx} />
    </div>
  );
}

export function PricingFields({ control, isFx }: Readonly<{ control: Ctrl; isFx: boolean }>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <TargetPriceFields control={control} isFx={isFx} />
      {!isFx && (
        <ComboboxField<RaiseFormValues>
          name="reference"
          label="Basis Type"
          options={BASIS_OPTIONS}
          searchable={false}
        />
      )}
    </div>
  );
}

export function MetaFields({ control }: Readonly<{ control: Ctrl }>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <FormField
        control={control}
        name="brokerPreference"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Broker Preference</FormLabel>
            <FormControl>
              <Input placeholder="e.g., StoneX" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <StatusField />
      <FormField
        control={control}
        name="notes"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Hedge Request Details</FormLabel>
            <FormControl>
              <Textarea rows={3} placeholder="Optional notes…" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

// Status is server-controlled at creation/edit time (always Pending Approval on
// raise); shown read-only to mirror the launchpad form layout.
function StatusField() {
  return (
    <FormItem>
      <FormLabel>Status</FormLabel>
      <div
        className="border-input bg-muted text-muted-foreground flex h-10 items-center rounded-md border px-3 text-sm"
        data-testid="raise-status"
      >
        Pending Approval
      </div>
    </FormItem>
  );
}
